import React from "react";
import SafeImage from "../../components/shared/SafeImage";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { Form, FormGroup, Label } from "reactstrap";
import Pagination from "react-js-pagination";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getTransactions, getTransactionsSummary } from "../../api/transactions";
import { formatDateTime } from "../../utils/formatDate";

/** Colour a status label so succeeded/pending/failed read at a glance. */
const STATUS_CLASS = {
  succeeded: "badge badge-success",
  pending: "badge badge-warning",
  failed: "badge badge-danger",
};

function money(amount, currency) {
  const value = Number(amount || 0).toFixed(2);
  return `$${value}${currency && currency !== "usd" ? ` ${currency.toUpperCase()}` : ""}`;
}

function Transactions(props) {
  const [dataTable, setDataTable] = React.useState({
    data: [],
    pagination: { totalRecords: 0, totalPages: 0, currentPage: 1, pageSize: 10 },
  });
  const [summary, setSummary] = React.useState(null);
  const [status, setStatus] = React.useState("");
  // A failed load must NOT fall through to the empty state: "No transactions
  // found" told the operator there is no data when the request had actually
  // errored (seen live: the list 500'd and the table calmly reported none).
  const [loadError, setLoadError] = React.useState(false);

  React.useEffect(() => {
    props.setTitlePage("Transactions");
  }, []);

  const loadSummary = async () => {
    try {
      setSummary(await props.getTransactionsSummary());
    } catch (error) {
      // Non-fatal: the table below is the primary content, and it reports its
      // own failure. Leaving `summary` null just hides the totals row.
      console.error("Failed to load the transactions summary:", error);
    }
  };

  const loadTransactions = async (page = 1) => {
    try {
      props.setLoading(true);
      setLoadError(false);
      const res = await props.getTransactions({
        page,
        pageSize: dataTable.pagination.pageSize,
        status,
      });
      setDataTable((prev) => ({ ...prev, ...res }));
    } catch (error) {
      console.error("Failed to load transactions:", error);
      setLoadError(true);
      setDataTable((prev) => ({ ...prev, data: [] }));
      toast("Couldn't load transactions. Please try again.", { type: "error" });
    } finally {
      props.setLoading(false);
    }
  };

  React.useEffect(() => {
    loadSummary();
  }, []);

  React.useEffect(() => {
    loadTransactions();
  }, [status]);

  const cards = summary
    ? [
        { label: "Gross Revenue", value: money(summary.grossRevenue, summary.currency), cls: "text-success" },
        { label: "Transactions", value: summary.totalCount },
        { label: "Succeeded", value: summary.succeededCount, cls: "text-success" },
        { label: "Pending", value: summary.pendingCount, cls: "text-warning" },
        { label: "Failed", value: summary.failedCount, cls: "text-danger" },
      ]
    : [];

  return (
    <>
      {/* Headline figures — 2-up on phones, equal-width from md up. */}
      <div className="row mb-2">
        {cards.map((c) => (
          <div className="col-6 col-md mb-2" key={c.label}>
            <div className="card h-100">
              <div className="card-body text-center py-3">
                <div className="text-muted" style={{ fontSize: "12px" }}>
                  {c.label}
                </div>
                <div className={`font-weight-bold ${c.cls || ""}`} style={{ fontSize: "20px" }}>
                  {c.value}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Status filter */}
      <Form className="card">
        <div className="card-body">
          <div className="row">
            <div className="col-sm-4">
              <FormGroup className="mb-0">
                <Label for="status">Status</Label>
                <select
                  name="status"
                  className="form-control form-control-sm"
                  id="status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="succeeded">Succeeded</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
              </FormGroup>
            </div>
          </div>
        </div>
      </Form>

      <div className="table-responsive">
        <table className="table table-sm table-bordered">
          <thead>
            <tr>
              <th></th>
              <th>Buyer</th>
              <th>Parlour</th>
              <th>Amount</th>
              <th>Method</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {dataTable.data.map((row) => (
              <tr key={`tr-${row.id}`}>
                <th className="text-center" style={{ verticalAlign: "middle" }}>
                  {row.buyerAvatarUrl ? (
                    <SafeImage
                      src={row.buyerAvatarUrl}
                      width={48}
                      className="img-thumbnail"
                      alt={row.buyerName || "buyer"}
                      loading="lazy"
                    />
                  ) : null}
                </th>
                <td style={{ verticalAlign: "middle" }}>{row.buyerName || "—"}</td>
                <td style={{ verticalAlign: "middle" }}>{row.parlourTitle}</td>
                <td style={{ verticalAlign: "middle" }}>{money(row.amount, row.currency)}</td>
                <td style={{ verticalAlign: "middle" }}>{row.paymentMethod || "—"}</td>
                <td style={{ verticalAlign: "middle" }}>
                  <span className={STATUS_CLASS[row.status] || "badge badge-secondary"}>
                    {row.status}
                  </span>
                </td>
                <td style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>
                  {formatDateTime(row.createdAt)}
                </td>
              </tr>
            ))}
            {dataTable.data.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center p-4">
                  {loadError ? (
                    <>
                      <div>Couldn&apos;t load transactions.</div>
                      <button
                        type="button"
                        className="btn btn-link p-0"
                        onClick={() => loadTransactions(dataTable.pagination.currentPage)}
                      >
                        Try again
                      </button>
                    </>
                  ) : (
                    "No transactions found."
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <Pagination
          activePage={dataTable.pagination.currentPage}
          itemsCountPerPage={dataTable.pagination.pageSize}
          totalItemsCount={dataTable.pagination.totalRecords}
          pageRangeDisplayed={5}
          onChange={loadTransactions}
        />
      </div>
    </>
  );
}

export default connect(null, { getTransactions, getTransactionsSummary })(
  withRouter(Transactions),
);
